import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { OrderDraft, OrderService, SubmittedOrder } from '@mypoz/shared';
import {
  changedOrderFields,
  normaliseOrderDetail,
  normaliseOrders,
  normaliseOrderStatuses,
  orderToDraft,
  toOrderCreateParams,
  toOrderModifyParams,
} from '@mypoz/shared';
import { useLocale } from '@mypoz/i18n';

import { isUnconfirmedFailure } from '@/features/packages/dictionary.js';

import {
  OrdersContext,
  type OrderCreateOutcome,
  type OrderDetailStatus,
  type OrderModifyOutcome,
  type OrderAutoPayOutcome,
  type OrderCancelOutcome,
  type OrderPayOutcome,
  type InvoiceOutcome,
  type PromoCheckOutcome,
  type OrderReceiveOutcome,
  type OrdersState,
} from './context.js';
import {
  createOrder,
  fetchInvoiceUrl,
  fetchOrderDetailRow,
  fetchOrderRows,
  fetchOrderStatusRows,
  modifyOrder,
  cancelVendorOrder,
  checkPromoCode,
  OrdersRequestError,
  payOrderFromWallet,
  receiveVendorOrder,
  setVendorOrderAutoPay,
  type PromoDiscount,
} from './api.js';

/**
 * Every order on the account, fetched once for the whole app (MYP-53).
 *
 * Structured after `PackagesProvider`, which wired the same shape for `package_list` in MYP-40:
 * raw rows in state, the vendor's status dictionary beside them, and normalisation in a `useMemo`
 * so switching language re-resolves rows already in memory instead of asking the vendor for data
 * that did not change.
 *
 * Three things make this one bigger than Packages':
 *
 * **Detail is lazy.** A summary row has no packages, no address and no timeline — the endpoint
 * simply does not send those columns without an `orderid`. There are 284 orders on the test
 * account, so detail is fetched per order when a card is expanded and cached here by order number.
 *
 * **Every write is real.** A new order goes through `orders_create` (MYP-91, `submit` below), an
 * edit through `order_modify` (MYP-86/MYP-48, `modify`), a payment through `orders_pay` (MYP-92,
 * `pay`), a confirm-receipt through `order_receive` (MYP-93) and a cancel through `order_cancel`
 * (MYP-94): send, then re-read — never changed in place. What the list shows is only ever what the
 * vendor last said.
 */
export function OrdersProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const [rows, setRows] = useState<readonly unknown[] | undefined>(undefined);
  const [statusRows, setStatusRows] = useState<readonly unknown[]>([]);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [dictionaryReady, setDictionaryReady] = useState(false);

  const [detailRows, setDetailRows] = useState<ReadonlyMap<string, unknown>>(new Map());
  const [detailStates, setDetailStates] = useState<ReadonlyMap<string, OrderDetailStatus>>(
    new Map(),
  );

  /**
   * Which orders' detail records have already been asked for, kept outside state on purpose.
   *
   * The in-flight check has to happen before the request is made, not inside a state updater —
   * React may run an updater twice, and a fetch started inside one is then fired twice. A ref is
   * read and written synchronously, so the second call of a StrictMode double-invoke sees the
   * first.
   */
  const requested = useRef(new Set<string>());

  useEffect(() => {
    // Guards both overlapping-fetch cases at once: StrictMode's dev double-mount, and a reload
    // pressed while the previous attempt is still running. Only the newest effect's result lands.
    let current = true;

    setRows(undefined);
    setFailed(false);
    setDictionaryReady(false);

    // The status dictionary lands on its own (MYP-126): the paged Orders screen labels its rows
    // with it and should not wait for the whole account's list.
    const dictionary = fetchOrderStatusRows().then((nextStatuses) => {
      if (!current) return;
      setStatusRows(nextStatuses);
      setDictionaryReady(true);
    });

    Promise.all([fetchOrderRows(), dictionary]).then(
      ([nextRows]) => {
        if (!current) return;
        setRows(nextRows);
        // Detail records describe orders as the vendor last sent them, so a deliberate refetch
        // should not keep serving the ones cached before it.
        requested.current = new Set();
        setDetailRows(new Map());
        setDetailStates(new Map());
      },
      (cause: unknown) => {
        if (!current) return;
        // Logged, not shown. The vendor's failure text is Chinese prose from its own code table:
        // useful to whoever is debugging, not something to put in front of a customer.
        console.error('[orders] could not load the order list', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  /**
   * The vendor's view of every order: summary rows, with a detail record layered over any order
   * whose card has been opened.
   *
   * The detail record replaces the summary outright rather than merging into it — it is the same
   * order from the same endpoint, carrying every column the summary had and forty more, so there
   * is nothing on the summary worth preserving and nothing that could go stale against it.
   */
  const vendorOrders = useMemo(() => {
    // The vendor has no Malay copy for its statuses, so ms reads the English side — the same
    // compromise `PackagesProvider` already makes, so the two lists stay consistent.
    const statuses = normaliseOrderStatuses(statusRows);
    const language = locale === 'zh' ? 'zh' : 'en';

    return normaliseOrders(rows ?? [], statuses, language).map((order) => {
      const detail = detailRows.get(order.orderNumber);
      if (detail === undefined) return order;

      const expanded = normaliseOrderDetail(detail, statuses, language);

      /*
        The record has to be the order that was asked for. `order_list` distinguishes its list mode
        from its detail mode by which parameter it gets, and a detail record that comes back naming
        a different order would replace this row with a copy of that one — two identical cards in
        the list and one order missing, with nothing on screen to say why. Cheap to check, and the
        failure it prevents is invisible.
      */
      if (expanded === undefined || expanded.orderNumber !== order.orderNumber) {
        console.error(
          `[orders] detail for ${order.orderNumber} came back as ${expanded?.orderNumber ?? 'nothing'}`,
        );
        return order;
      }

      return expanded;
    });
  }, [rows, statusRows, detailRows, locale]);

  /**
   * Held in a ref so the callbacks below can read the current list without being rebuilt on every
   * fetch. A `loadDetail` whose identity changed each render would re-fire the effect in every
   * expanded card, which is the opposite of caching the detail.
   */
  const vendorOrdersRef = useRef(vendorOrders);
  vendorOrdersRef.current = vendorOrders;

  const loadDetail = useCallback((orderNumber: string) => {
    // Already fetched, or a fetch is in flight. Re-opening a card must cost nothing.
    if (requested.current.has(orderNumber)) return;

    const order = vendorOrdersRef.current.find((item) => item.orderNumber === orderNumber);
    const vendorOrderId = order?.vendorOrderId;

    // A row without the vendor's id cannot be asked for in detail; it stays a summary.
    if (vendorOrderId === undefined) return;

    requested.current.add(orderNumber);
    setDetailStates((current) => new Map(current).set(orderNumber, 'loading'));

    void fetchOrderDetailRow(vendorOrderId).then(
      (row) => {
        setDetailRows((current) => new Map(current).set(orderNumber, row));
        setDetailStates((current) => new Map(current).set(orderNumber, 'ready'));
      },
      (cause: unknown) => {
        console.error(`[orders] could not load detail for ${orderNumber}`, cause);
        // Dropped from the set so the panel's Retry can ask again; the state says error until it
        // does, which is what the panel renders the retry from.
        requested.current.delete(orderNumber);
        setDetailStates((current) => new Map(current).set(orderNumber, 'error'));
      },
    );
  }, []);

  /**
   * Reads one order's detail again and stores it, without passing through `loading` — the edit
   * form is open over this order, and a loading state would swap it for a spinner mid-save.
   * Returns the order as it now stands, or undefined if the read failed (the cached copy stays).
   */
  const reread = useCallback(
    async (
      orderNumber: string,
      vendorOrderId: string,
    ): Promise<{ order: SubmittedOrder; row: unknown } | undefined> => {
      let row: unknown;
      try {
        row = await fetchOrderDetailRow(vendorOrderId);
      } catch (cause) {
        console.error(`[orders] could not re-read ${orderNumber}`, cause);
        return undefined;
      }

      // Labels do not matter to any caller here, so no status dictionary is needed.
      const order = normaliseOrderDetail(row, [], 'en');
      if (order === undefined || order.orderNumber !== orderNumber) {
        console.error(`[orders] re-read of ${orderNumber} came back as another record`);
        return undefined;
      }
      return { order, row };
    },
    [],
  );

  /** Makes a re-read record the one every screen shows for this order. */
  const storeDetail = useCallback((orderNumber: string, row: unknown) => {
    requested.current.add(orderNumber);
    setDetailRows((current) => new Map(current).set(orderNumber, row));
    setDetailStates((current) => new Map(current).set(orderNumber, 'ready'));
  }, []);

  const modify = useCallback(
    async (orderNumber: string, draft: OrderDraft): Promise<OrderModifyOutcome> => {
      const opened = vendorOrdersRef.current.find((item) => item.orderNumber === orderNumber);
      const vendorOrderId = opened?.vendorOrderId;
      if (opened === undefined || vendorOrderId === undefined || !opened.modifyBase) {
        return { kind: 'cannotSave' };
      }

      /*
        Read before writing. The form may have been open for a while, and the vendor rejects an
        edit whose `status` no longer matches — but its rejection is one `status: 0` among many,
        with the reason only in Chinese prose. Checking here is what lets the screen say plainly
        "this order changed" and show the current one, with nothing sent. It also means every
        field sent back below is the vendor's latest, not what was cached when the card opened.
      */
      const before = await reread(orderNumber, vendorOrderId);
      if (before === undefined) return { kind: 'notSent', code: 'REREAD_FAILED' };
      const fresh = before.order;

      const movedOn =
        fresh.statusCode !== opened.statusCode ||
        changedOrderFields(opened, orderToDraft(fresh)).length > 0;
      if (movedOn) {
        storeDetail(orderNumber, before.row);
        return { kind: 'stale', current: fresh };
      }

      const params = toOrderModifyParams(fresh, draft);
      if (params === undefined) return { kind: 'cannotSave' };

      try {
        await modifyOrder(params);
      } catch (cause) {
        console.error(`[orders] order_modify refused for ${orderNumber}`, cause);
        const code = cause instanceof OrdersRequestError ? cause.code : 'UNKNOWN';

        /*
          Re-read either way. A lost answer may have landed; and status 0 includes "a service or
          package failed", which can leave part of the edit applied. Only this order is re-read,
          not the whole list — the customer is still on this form, and a list reload would drop
          every cached detail record out from under it.
        */
        const after = await reread(orderNumber, vendorOrderId);
        if (after) storeDetail(orderNumber, after.row);

        if (code === 'UNKNOWN' || isUnconfirmedFailure(code)) return { kind: 'unconfirmed', code };

        /*
          The check above leaves a small window: the warehouse can move the order between that read
          and this write, and the vendor then refuses with its generic status 0. If the re-read
          shows the status moved, that is what happened, and it is said as such.
        */
        if (after && after.order.statusCode !== fresh.statusCode) {
          return { kind: 'stale', current: after.order };
        }

        return {
          kind: 'refused',
          code,
          detail: cause instanceof OrdersRequestError ? cause.detail : '',
        };
      }

      // Saved. The whole list is re-read: the summary row's status and fees may have moved too.
      reload();
      return { kind: 'saved' };
    },
    [reread, storeDetail, reload],
  );

  const detailStatus = useCallback(
    (orderNumber: string): OrderDetailStatus => detailStates.get(orderNumber) ?? 'idle',
    [detailStates],
  );

  /**
   * `orders_create`, then a re-read. In flight at most once: two clicks can land before the button
   * re-renders disabled, and a second request here is a second real order.
   */
  const submitting = useRef(false);

  const submit = useCallback(
    async (
      draft: OrderDraft,
      warehouseId: string,
      services: readonly OrderService[],
      receiptPaths: Readonly<Record<string, readonly string[]>> = {},
    ): Promise<OrderCreateOutcome> => {
      const params = toOrderCreateParams(draft, warehouseId, services, receiptPaths);
      if (params === undefined || submitting.current) return { kind: 'notSent' };

      submitting.current = true;
      try {
        const orderNumber = await createOrder(params);
        reload();
        return { kind: 'created', orderNumber };
      } catch (cause) {
        console.error('[orders] orders_create did not create an order', cause);
        const code = cause instanceof OrdersRequestError ? cause.code : 'UNKNOWN';

        if (code === 'UNKNOWN' || isUnconfirmedFailure(code)) {
          // It may have landed. The list is re-read so the new order shows if it did.
          reload();
          return { kind: 'unconfirmed', code };
        }

        return {
          kind: 'refused',
          code,
          detail: cause instanceof OrdersRequestError ? cause.detail : '',
        };
      } finally {
        submitting.current = false;
      }
    },
    [reload],
  );

  /** One payment in flight at a time: a second request here is a second real debit. */
  const paying = useRef(false);

  const pay = useCallback(
    async (
      orderNumber: string,
      payPassword: string,
      promo?: PromoDiscount,
    ): Promise<OrderPayOutcome> => {
      const order = vendorOrdersRef.current.find((item) => item.orderNumber === orderNumber);
      if (order === undefined || paying.current) return { kind: 'notSent' };
      // A code applies once: never send a second one for an order that already carries a code.
      if (promo !== undefined && order.couponCode.trim() !== '') return { kind: 'notSent' };

      paying.current = true;
      try {
        await payOrderFromWallet(orderNumber, payPassword, promo);
        return { kind: 'paid' };
      } catch (cause) {
        const code = cause instanceof OrdersRequestError ? cause.code : 'UNKNOWN';
        // Logged by code only. The request carried the payment password, and nothing about it belongs in a log.
        console.error(`[orders] orders_pay did not pay ${orderNumber} (code ${code})`);

        if (code === '2') return { kind: 'wrongPassword' };
        if (code === '3') return { kind: 'insufficient' };

        if (code === 'UNKNOWN' || isUnconfirmedFailure(code)) {
          // It may have gone through. Ask the order itself rather than guess, and never re-send.
          const vendorOrderId = order.vendorOrderId;
          const after =
            vendorOrderId === undefined ? undefined : await reread(orderNumber, vendorOrderId);
          return {
            kind: 'unconfirmed',
            code,
            nowPaid: after === undefined ? undefined : after.order.status !== 'unpaid',
          };
        }

        return {
          kind: 'refused',
          code,
          detail: cause instanceof OrdersRequestError ? cause.detail : '',
        };
      } finally {
        paying.current = false;
      }
    },
    [reread],
  );

  /** One auto-pay switch in flight at a time. */
  const switchingAutoPay = useRef(false);

  /**
   * `ajaxUpdateUserOrderPayment` for one order (MYP-97), then a re-read of that order in place so
   * its payment method is the vendor's. A refusal is any answer but 1, including the live code-less
   * `{msg}`; only a transport failure counts as a lost answer, and that is checked, never re-sent.
   */
  const setAutoPay = useCallback(
    async (orderNumber: string): Promise<OrderAutoPayOutcome> => {
      const order = vendorOrdersRef.current.find((item) => item.orderNumber === orderNumber);
      if (order === undefined || switchingAutoPay.current) return { kind: 'notSent' };

      switchingAutoPay.current = true;
      const readBack = async () => {
        const vendorOrderId = order.vendorOrderId;
        const after =
          vendorOrderId === undefined ? undefined : await reread(orderNumber, vendorOrderId);
        if (after) storeDetail(orderNumber, after.row);
        return after;
      };
      try {
        await setVendorOrderAutoPay(orderNumber);
        await readBack();
        return { kind: 'set' };
      } catch (cause) {
        console.error(`[orders] ajaxUpdateUserOrderPayment did not switch ${orderNumber}`, cause);
        const code = cause instanceof OrdersRequestError ? cause.code : 'NETWORK';
        if (!(cause instanceof OrdersRequestError) || isUnconfirmedFailure(code)) {
          const after = await readBack();
          return {
            kind: 'unconfirmed',
            nowAuto: after === undefined ? undefined : after.order.paymentMode === 'AUTO',
          };
        }
        return {
          kind: 'refused',
          code,
          detail: cause instanceof OrdersRequestError ? cause.detail : '',
        };
      } finally {
        switchingAutoPay.current = false;
      }
    },
    [reread, storeDetail],
  );

  /**
   * `OrderCodenoCheck` for one unpaid order (MYP-95), with the order's own channel and billing
   * weight: the only point in an order's life when both are real. Read-only, so it is safe to
   * repeat. The vendor's reason for a refusal is Chinese only and goes to the log.
   */
  const checkPromo = useCallback(
    async (orderNumber: string, code: string): Promise<PromoCheckOutcome> => {
      const order = vendorOrdersRef.current.find((item) => item.orderNumber === orderNumber);
      const trimmed = code.trim();
      const grams = order?.chargeableWeightGrams;
      if (
        order === undefined ||
        trimmed === '' ||
        order.couponCode.trim() !== '' ||
        order.channelId === undefined ||
        grams === undefined ||
        grams <= 0
      ) {
        return { kind: 'notAvailable' };
      }

      try {
        const discountCents = await checkPromoCode(trimmed, order.channelId, String(grams / 1000));
        return { kind: 'valid', promo: { code: trimmed, discountCents } };
      } catch (cause) {
        const errorCode = cause instanceof OrdersRequestError ? cause.code : 'UNKNOWN';
        console.error(`[orders] OrderCodenoCheck refused a code for ${orderNumber}`, cause);
        return errorCode === '-1' ? { kind: 'invalid' } : { kind: 'failed' };
      }
    },
    [],
  );

  /**
   * Re-reads one order in place. Not `reload`: that clears the list while it loads, which unmounts
   * every card — and with it any dialog open on one.
   */
  const refreshOrder = useCallback(
    (orderNumber: string) => {
      const vendorOrderId = vendorOrdersRef.current.find(
        (item) => item.orderNumber === orderNumber,
      )?.vendorOrderId;
      if (vendorOrderId === undefined) return;
      void reread(orderNumber, vendorOrderId).then((after) => {
        if (after) storeDetail(orderNumber, after.row);
      });
    },
    [reread, storeDetail],
  );

  /** One cancel in flight at a time: it cannot be undone, so it is never sent twice. */
  const cancelling = useRef(false);

  /**
   * `order_cancel` (MYP-94), then a re-read of that one order in place, the same way as
   * `confirmReceipt`. The packages it releases are the caller's to reload: this provider sits
   * outside `PackagesProvider` and cannot reach it.
   */
  const cancel = useCallback(
    async (orderNumber: string): Promise<OrderCancelOutcome> => {
      const order = vendorOrdersRef.current.find((item) => item.orderNumber === orderNumber);
      if (order === undefined || cancelling.current) return { kind: 'notSent' };

      cancelling.current = true;
      try {
        await cancelVendorOrder(orderNumber);

        const vendorOrderId = order.vendorOrderId;
        const after =
          vendorOrderId === undefined ? undefined : await reread(orderNumber, vendorOrderId);
        if (after) storeDetail(orderNumber, after.row);
        else reload();

        return { kind: 'cancelled' };
      } catch (cause) {
        const code = cause instanceof OrdersRequestError ? cause.code : 'UNKNOWN';
        const detail = cause instanceof OrdersRequestError ? cause.detail : '';
        // The vendor's text goes to the log only; the customer gets a translated line.
        console.error(`[orders] order_cancel did not cancel ${orderNumber} (code ${code})`, detail);

        if (code === 'UNKNOWN' || isUnconfirmedFailure(code)) {
          // It may have gone through. Ask the order itself rather than guess, and never re-send.
          const vendorOrderId = order.vendorOrderId;
          const after =
            vendorOrderId === undefined ? undefined : await reread(orderNumber, vendorOrderId);
          if (after) storeDetail(orderNumber, after.row);
          return {
            kind: 'unconfirmed',
            code,
            nowCancelled: after === undefined ? undefined : after.order.status === 'cancelled',
          };
        }

        return { kind: 'refused', code, detail };
      } finally {
        cancelling.current = false;
      }
    },
    [reread, storeDetail, reload],
  );

  /** One confirm-receipt in flight at a time: it cannot be undone, so it is never sent twice. */
  const receiving = useRef(false);

  /**
   * `order_receive` (MYP-93), then a re-read of that one order in place, so the card shows the
   * vendor's own status and the change survives a reload. Not `reload`: that clears the list while
   * it loads, which unmounts every card. If the re-read itself fails, the whole list is asked for
   * again instead, so the order is not left showing as shipped.
   */
  const confirmReceipt = useCallback(
    async (orderNumber: string): Promise<OrderReceiveOutcome> => {
      const order = vendorOrdersRef.current.find((item) => item.orderNumber === orderNumber);
      if (order === undefined || receiving.current) return { kind: 'notSent' };

      receiving.current = true;
      try {
        await receiveVendorOrder(orderNumber);

        const vendorOrderId = order.vendorOrderId;
        const after =
          vendorOrderId === undefined ? undefined : await reread(orderNumber, vendorOrderId);
        if (after) storeDetail(orderNumber, after.row);
        else reload();

        return { kind: 'received' };
      } catch (cause) {
        const code = cause instanceof OrdersRequestError ? cause.code : 'UNKNOWN';
        const detail = cause instanceof OrdersRequestError ? cause.detail : '';
        // The vendor's text goes to the log only; the customer gets a translated line.
        console.error(
          `[orders] order_receive did not confirm ${orderNumber} (code ${code})`,
          detail,
        );

        if (code === 'UNKNOWN' || isUnconfirmedFailure(code)) {
          // It may have gone through. Ask the order itself rather than guess, and never re-send.
          const vendorOrderId = order.vendorOrderId;
          const after =
            vendorOrderId === undefined ? undefined : await reread(orderNumber, vendorOrderId);
          if (after) storeDetail(orderNumber, after.row);
          return {
            kind: 'unconfirmed',
            code,
            nowReceived: after === undefined ? undefined : after.order.status === 'received',
          };
        }

        return { kind: 'refused', code, detail };
      } finally {
        receiving.current = false;
      }
    },
    [reread, storeDetail, reload],
  );

  const orders = vendorOrders;

  /**
   * Asks the vendor for an order's invoice link (MYP-124). Read-only, so no in-flight guard of its
   * own — the card disables its button while it waits. Logged by code only.
   */
  const requestInvoice = useCallback(async (orderNumber: string): Promise<InvoiceOutcome> => {
    try {
      return { kind: 'ready', url: await fetchInvoiceUrl(orderNumber) };
    } catch (cause) {
      const code = cause instanceof OrdersRequestError ? cause.code : 'UNKNOWN';
      console.error(
        `[orders] get_receipt_pdf_url gave no invoice for ${orderNumber} (code ${code})`,
      );
      return code === '-2' ? { kind: 'notFound' } : { kind: 'failed' };
    }
  }, []);

  const value = useMemo<OrdersState>(
    () => ({
      orders,
      status: rows ? 'ready' : failed ? 'error' : 'loading',
      dictionaryStatus: dictionaryReady ? 'ready' : failed ? 'error' : 'loading',
      toOrders: (pageRows: readonly unknown[]) =>
        normaliseOrders(
          pageRows,
          normaliseOrderStatuses(statusRows),
          locale === 'zh' ? 'zh' : 'en',
        ),
      toOrderFromDetail: (record: unknown) =>
        normaliseOrderDetail(
          record,
          normaliseOrderStatuses(statusRows),
          locale === 'zh' ? 'zh' : 'en',
        ),
      listVersion: attempt,
      reload,
      loadDetail,
      detailStatus,
      submit,
      modify,
      pay,
      checkPromo,
      setAutoPay,
      refreshOrder,
      cancel,
      confirmReceipt,
      requestInvoice,
    }),
    [
      orders,
      rows,
      statusRows,
      locale,
      attempt,
      dictionaryReady,
      failed,
      reload,
      loadDetail,
      detailStatus,
      submit,
      modify,
      pay,
      checkPromo,
      setAutoPay,
      refreshOrder,
      cancel,
      confirmReceipt,
      requestInvoice,
    ],
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
}
